import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import SearchTemplate from "./SearchTemplate";
import SelectTemplate from "./SelectTemplate";
import { useDispatch, useSelector } from "react-redux";
import {
  bulkUpload,
  surveyAdd,
  surveySelector,
} from "../../../redux/surveySlice";
import * as XLSX from "xlsx";
import { fieldsSelector } from "../../../redux/fieldsSlice";
import AdminInputs from "../AdminInputs";

import Dropdown from "react-bootstrap/Dropdown";
import { Icon } from "@iconify/react";
import {
  CSV_EXPORT,
  FIELDS,
  FIELDS_REQUIRED,
} from "../../../constants/constants";
import { CSVLink } from "react-csv";
import readXlsxFile from "read-excel-file";
import { error, success } from "../../../redux/notificationSlice";
import { Link } from "react-router-dom";
import {
  isValidUploadDate,
  normalizeUploadDate,
} from "../../../utils/uploadDate";

const CreateSurvey = ({ onHide, show }) => {
  const dispatch = useDispatch();
  const survey = useSelector(surveySelector);
  const [selected, setSelected] = useState("template");
  const [file, setFile] = useState("");
  const [bulk, setBulk] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const { fields } = useSelector(fieldsSelector);
  const keys = Object.keys(CSV_EXPORT[0]);
  const Headers = FIELDS.map((field, index) => {
    return { label: field, key: keys[index] };
  });

  const addSurvey = async () => {
    let empty = [];
    fields.forEach((element) => {
      if (element.isRequired && !survey[element.name]) {
        empty.push(`${element.title} is required`);
      }
      if (element.type === "number" && parseInt(survey[element.name]) < 0) {
        empty.push(`${element.title} must be greater than 1`);
      }
    });
    if (empty.length > 0) {
      dispatch(error(empty[0]));
      return;
    }
    dispatch(surveyAdd(survey));
    setSelected("template");
  };

  const hide = () => {
    setSelected("template");
    setBulk(false);
    onHide();
  };

  const handleFileUpload = async (e) => {
    let errors = [];
    let surveyList = [];
    const file = e.target.files[0];
    
    if (!file) {
      dispatch(error("Please select a file"));
      return;
    }

    // Determine file type
    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isCSV && !isExcel) {
      dispatch(error("Please upload a CSV or Excel file"));
      return;
    }

    try {
      if (isCSV) {
        // Handle CSV files using XLSX library
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const csvData = event.target.result;
            const workbook = XLSX.read(csvData, { type: 'string' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (rows.length < 2) {
              dispatch(error("CSV file must have at least a header row and one data row"));
              return;
            }

            const headers = rows[0]; // First row is headers in CSV
            
            // Validate required fields
            FIELDS_REQUIRED.forEach((field) => {
              if (!headers.includes(field)) {
                errors.push(`${field} is required`);
              }
            });

            // Process data rows (starting from row 1)
            for (let i = 1; i < rows.length; i++) {
              const data = rows[i];
              if (data.length === 0 || data.every(cell => !cell)) continue; // Skip empty rows
              
              let object = {};
              keys.forEach((key, index) => {
                let value = data[headers.indexOf(FIELDS[index])];
                
                // Check if this is a date field and convert if necessary
                if (
                  FIELDS[index] === "Inspection Date" ||
                  FIELDS[index] === "Previous Valuation Date"
                ) {
                  value = normalizeUploadDate(value);
                  
                  // Validate date format
                  if (!isValidUploadDate(value)) {
                    errors.push(
                      `Invalid date in row ${i + 1} for "${FIELDS[index]}". Use a short date such as DD/MM/YYYY.`,
                    );
                  }
                }
                
                object[key] = value;
              });
              object["templateId"] = survey["templateId"];
              object["templateName"] = survey["templateName"];
              object["status"] = "draft";

              surveyList.push(object);
            }

            if (errors.length > 0) {
              dispatch(error(errors[0]));
              // Reset file input to allow re-upload
              e.target.value = '';
              return;
            } else {
              setFile(file.name);
              setSurveys(surveyList);
            }
          } catch (csvError) {
            dispatch(error("Failed to parse CSV file"));
          }
        };
        reader.readAsText(file);
      } else {
        // Handle Excel files using readXlsxFile
        readXlsxFile(file).then((rows) => {
          const headers = rows[4]; // Row 5 contains headers in Excel template
          
          FIELDS_REQUIRED.forEach((field) => {
            if (!headers.includes(field)) {
              errors.push(`${field} is required`);
            }
          });
          
          for (let i = 5; i < rows.length; i++) { // Data starts from row 6
            const data = rows[i];
            let object = {};
            keys.forEach((key, index) => {
              let value = data[headers.indexOf(FIELDS[index])];
              
              // Check if this is a date field and convert if necessary
              if (
                FIELDS[index] === "Inspection Date" ||
                FIELDS[index] === "Previous Valuation Date"
              ) {
                value = normalizeUploadDate(value);
                
                // Validate date format
                if (!isValidUploadDate(value)) {
                  errors.push(
                    `Invalid date in row ${i + 1} for "${FIELDS[index]}". Use an Excel date cell or a short date such as DD/MM/YYYY.`,
                  );
                }
              }
              
              object[key] = value;
            });
            object["templateId"] = survey["templateId"];
            object["templateName"] = survey["templateName"];
            object["status"] = "draft";

            surveyList.push(object);
          }
          
          if (errors.length > 0) {
            dispatch(error(errors[0]));
            // Reset file input to allow re-upload
            document.getElementById('file').value = '';
            return;
          } else {
            setFile(file.name);
            setSurveys(surveyList);
          }
        }).catch(() => {
          dispatch(
            error(
              "Failed to read file. Please check the file format and try again.",
            ),
          );
        });
      }
    } catch {
      dispatch(error("Failed to process file"));
    }
  };

  const submitBulkUpload = () => {
    if (surveys.length > 0) {
      dispatch(bulkUpload({ surveys }));
    } else dispatch(error("Please select file to upload surveys in bulk"));
  };

  const handleBulkClick = () => {
    if (survey.templateId) {
      setBulk(true);
    } else {
      dispatch(error("Please select template to add surveys in bulk"));
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => hide()} className="modalPopup" centered>
        <Modal.Header>
          <div className="media-body">
            <h2>Create Survey</h2>
            <p>Choose your template to start creating survey</p>
          </div>
          <button className="cta-text" onClick={() => hide()}>
            Cancel
          </button>
        </Modal.Header>
        <Modal.Body className="show-grid">
          {/* STEP 1*/}
          {selected === "template" ? (
            <div className="stepContent">
              <SearchTemplate />
              <SelectTemplate />
            </div>
          ) : (
            <div className="stepContent">
              <div className="templateTitle">
                <strong>Template:</strong> {survey.templateName}
              </div>
              <div className="fieldWrapper">
                {fields.map((field) => (
                  <AdminInputs key={field.id} field={field} />
                ))}
              </div>
            </div>
          )}
          {file && (
            <div className="mediaFile">
              <div className="icon">
                <Icon icon="bi:file-text" />
              </div>
              <div className="media-body">{file}</div>
              <div className="action">
                <Link
                  to="#"
                  onClick={() => {
                    setFile("");
                    setSurveys([]);
                  }}
                >
                  <Icon icon="mdi:delete-outline" />
                </Link>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <ul className="stepCreate">
            <li
              className={selected === "template" ? "active" : ""}
              onClick={() => setSelected("template")}
            >
              <span>Select Template</span>
            </li>
            {!bulk && (
              <li
                className={selected === "survey" ? "active" : ""}
                onClick={() => setSelected("survey")}
              >
                <span>Add Survey Details</span>
              </li>
            )}
          </ul>

          <div className="btnDropGroup">
            {selected === "template" && bulk && (
              <>
                <div className="fileBtn upload-btn me-2">
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e)}
                    className="fileUpload"
                    id="file"
                    accept=".csv,.xlsx,.xls"
                  />
                  <label htmlFor="file" className="btn btn-indigo px-3">
                    <span className="p-0">Select File</span>
                    <span className="icon">
                      {" "}
                      <Icon icon="ph:selection-background" />
                    </span>
                  </label>
                  {/* <Dropdown autoClose="outside" align="end">
                    <Dropdown.Toggle
                      variant="indigo"
                      bsPrefix="icon"
                      id="dropdown-basic"
                    >
                      <i>
                        <Icon icon="material-symbols:keyboard-arrow-down-rounded" />
                      </i>
                    </Dropdown.Toggle>
                     <Dropdown.Menu className="p-2">
                       <Dropdown.Item className="p-2" role="button" href="#">
                      <CSVLink
                        filename="template.csv"
                        data={CSV_EXPORT}
                        headers={Headers}
                      >
                        Download Template
                        <i className="ms-2">
                          <Icon icon="uil:download-alt" />
                        </i>
                      </CSVLink>
                    </Dropdown.Menu>
                  </Dropdown> */}
                </div>
                <button
                  onClick={() => submitBulkUpload()}
                  className="btn cta-primary"
                >
                  Save
                </button>
              </>
            )}
          </div>
          <div className="cta-form">
            {selected === "template" && !bulk && (
              <>
                <div className="upload-btn me-2">
                  {/* <input type="file" className="fileUpload" /> */}
                  <button
                    onClick={() => handleBulkClick()}
                    className="btn btn-indigo m-0"
                  >
                    <span> Bulk Upload</span>
                    <i className="ms-2">
                      <Icon icon="uil:upload-alt" />
                    </i>
                  </button>
                </div>
                <button
                  onClick={() => setSelected("survey")}
                  className="btn cta-primary"
                >
                  Next
                </button>
              </>
            )}
            {selected === "survey" && (
              <>
                <button
                  className="btn cta-primary-light"
                  onClick={() => setSelected("template")}
                >
                  Back
                </button>
                <button onClick={() => addSurvey()} className="btn cta-primary">
                  Submit
                </button>
              </>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateSurvey;
